import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { EditPolicyComposer } from "@/forms/rema/editPolicy/composer"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { FileKey, Loader2 } from "lucide-react"
import { useToast } from "@/contexts/ToastContext"

function EditPolicyPage() {
  const { policyNumber } = Route.useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  // Intentionally fetches the full list then finds client-side. reMa has no
  // getPolicyByNumber endpoint, and getPolicies returns the entire table (no
  // server-side pagination), so the record is always present client-side and the
  // list is normally already in cache from the list view. This is NOT the
  // pagination bug fixed on the other domains — switch to a single-record fetch
  // only once the backend adds getPolicyByNumber.
  const { data, isLoading } = useApiQuery("getPolicies", {})
  const policy = ((data as any)?.content?.policies || (data as any)?.policies || []).find(
    (p: any) => String(p.policyNumber) === String(policyNumber)
  )

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-4">
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <FileKey className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl">Policy</CardTitle>
              <CardDescription className="text-base">Update policy configuration</CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
      <Card className="shadow-sm">
        <CardContent className="p-6">
          <EditPolicyComposer
            showHeader={false}
            initialValues={{
              policyNumber: policy?.policyNumber,
              policyUri: policy?.policyUri ?? '',
              version: policy?.version ?? '',
              role: policy?.role ?? '',
              allow: policy?.allow ?? [],
              deny: policy?.deny ?? [],
              resourceInstances: policy?.resourceInstances ?? [],
              conditions: policy?.conditions ?? {},
            }}
            submitLabel="Save Changes"
            onSuccess={() => navigate({ to: '/rema/policies' })}
            onError={(error) => toast({ title: 'Error', description: `Failed to update policy: ${error.message}`, type: 'error' })}
            onCancel={() => navigate({ to: '/rema/policies' })}
          />
        </CardContent>
      </Card>
    </div>
  )
}

export const Route = createFileRoute("/rema/policies/$policyNumber_/edit")({
  staticData: {
    title: 'Edit Policy',
    isSecured: true,
  },
  component: EditPolicyPage,
})
